import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { ModelEntityType } from '@upright/common/model/input/types';
import { Contributions, FinalScoreValence } from '@upright/common/model/output/scores/drilldowns/types';
import { Valence } from '@upright/common/model/types';
import { StringHash } from '@upright/common/utils/string-hash/types';
import { reactQueryStatusToRequestStatus, RequestStatus } from '@upright/ui/api/common/types';
import { BouLink } from '@upright/ui/common/generic/content/texts/bou-link';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { getModelEntityUrl } from '@upright/ui/untidy/urls';
import { useCompanyDrilldownContributions } from '@upright/ui/views/model-entity-views/company-view/drilldown/use-get-drilldown-contributions';
import { keyBy, mapValues } from 'lodash';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

interface ProductContribution {
  impact: ImpactCategoryId;
  valence: Valence;
  productCode: string;
  productTitle: string;
  value: number;
}

function contributionsToList(
  contributions: Contributions,
  impact: ImpactCategoryId,
  valence: Valence,
  productNamesByCode: StringHash,
) {
  const ret: ProductContribution[] = [];
  const contributionsByProduct = contributions[impact]?.[valence as FinalScoreValence] ?? {};
  Object.keys(contributionsByProduct).forEach(productCode => {
    const value = contributionsByProduct[productCode];
    const productTitle = productNamesByCode[productCode];
    ret.push({ productCode, productTitle, impact, valence, value: value.contribution || 0 });
  });
  return ret.sort((a, b) => b.value - a.value);
}

export function CompanyContributingProductList() {
  const context = useNetImpactLensContext();
  const selection = context?.state;
  const impact = selection?.impact;
  const valence = selection?.valence;
  const modelResultId = context?.data?.modelResultId as string | undefined;
  const productViewAccess = context?.data?.hasProductViewAccess;
  const drilldownQuery = useCompanyDrilldownContributions(modelResultId);
  const productNames = context?.data?.productNames;

  if (!impact || !valence || !modelResultId) {
    return (
      <>
        no selection {impact} {valence} {modelResultId}
      </>
    );
  }

  const contributions = drilldownQuery.data;

  if (
    reactQueryStatusToRequestStatus({
      status: drilldownQuery.status,
      fetchStatus: drilldownQuery.fetchStatus,
    }) === RequestStatus.LOADING
  ) {
    return <Skeleton width="10em" />;
  } else if (!contributions) {
    return <>Failed to load contributions data</>;
  }

  const productNamesByCode = mapValues(
    keyBy(productNames, d => d.id),
    d => d.name,
  );
  const contributionList = contributionsToList(contributions, impact, valence, productNamesByCode).slice(0, 5);

  const items = contributionList.map((d, index) => {
    function getInner() {
      if (!productViewAccess || context.options.disableLinks) {
        return d.productTitle;
      }
      return (
        <BouLink to={getModelEntityUrl(ModelEntityType.PRODUCT, d.productCode, d.productTitle)}>
          {d.productTitle || d.productCode}
        </BouLink>
      );
    }

    return <Item key={index}>{getInner()}</Item>;
  });

  if (items.length === 1) {
    return <div>Top contributing product: {items}</div>;
  } else if (items.length > 1) {
    return (
      <div>
        <Title>Top contributing products:</Title>
        <List>{items}</List>
      </div>
    );
  } else {
    return <></>;
  }
}

const Title = styled.div`
  margin-bottom: 0.65em;
  font-weight: 500;
`;

const List = styled.ul`
  margin-inline-start: 1em !important;
  li + li {
    margin-top: 0.4em;
  }
`;

const Item = styled.li``;
